import * as d3 from 'd3';
import type { DealerGexHeatmap } from '../../services/dealerGexHeatmapService';
import { earningsCaption } from '../../services/dealerGexHeatmapService';
import { FIELD_DOWNSAMPLE, HEATMAP_COLORS, HEATMAP_MARGIN } from './constants';
import {
  fieldColor,
  isWeekendIso,
  paintField,
  rgbCss,
  terrainScale,
  zMaxOf,
} from './fieldRaster';
import { gammaRidges, zeroContours, type RidgeFit, type RidgePoint } from './ridges';


function dateIndex(dates: string[], iso: string): number {
  return dates.indexOf(iso);
}

function weekdayTicks(dates: string[]): number[] {
  const weekdays: number[] = [];
  for (let i = 0; i < dates.length; i++) {
    const iso = dates[i];
    if (iso && !isWeekendIso(iso)) weekdays.push(i);
  }
  const step =
    weekdays.length > 36 ? 4 : weekdays.length > 24 ? 3 : weekdays.length > 12 ? 2 : 1;
  return weekdays.filter((_, k) => k % step === 0);
}

/** One cash session, 09:00–16:00. Sigma levels stay horizontal. */
export interface TerrainClock {
  times: string[];
  plus2: number;
  plus1: number;
  minus1: number;
  minus2: number;
  path: (number | null)[];
  asof: string | null;
}

function minutesOf(hhmm: string): number {
  const match = /^(\d{2}):(\d{2})$/.exec(hhmm);
  if (!match) return 0;
  return Number(match[1]) * 60 + Number(match[2]);
}

function medianStrikeGap(strikes: number[]): number {
  if (strikes.length < 2) return 1;
  const gaps: number[] = [];
  for (let i = 1; i < strikes.length; i++) gaps.push(Math.abs(strikes[i]! - strikes[i - 1]!));
  gaps.sort((a, b) => a - b);
  return gaps[Math.floor(gaps.length / 2)] || 1;
}

function columnAt(times: string[], hhmm: string): number {
  const exact = times.indexOf(hhmm);
  if (exact >= 0) return exact;
  const target = minutesOf(hhmm);
  let best = 0;
  let bestDist = Number.POSITIVE_INFINITY;
  for (let i = 0; i < times.length; i++) {
    const dist = Math.abs(minutesOf(times[i] ?? '') - target);
    if (dist < bestDist) {
      bestDist = dist;
      best = i;
    }
  }
  return best;
}

function formatMdTick(iso: string): string {
  const mdy = /^(\d{4})-(\d{2})-(\d{2})$/.exec(iso);
  if (!mdy) return iso;
  const months = [
    'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
    'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec',
  ];
  const month = months[Number(mdy[2]) - 1] ?? mdy[2];
  return `${month} ${Number(mdy[3])}`;
}

export function drawHeatmap(
  canvas: HTMLCanvasElement,
  svgEl: SVGSVGElement,
  data: DealerGexHeatmap,
  width: number,
  height: number,
  clipId: string,
  field: number[][] = data.z,
  clock: TerrainClock | null = null,
): void {
  const svg = d3.select(svgEl);
  svg.selectAll('*').remove();

  const ctx = canvas.getContext('2d');
  const dpr = typeof window !== 'undefined' ? window.devicePixelRatio || 1 : 1;
  canvas.width = Math.max(0, Math.round(width * dpr));
  canvas.height = Math.max(0, Math.round(height * dpr));
  canvas.style.width = `${width}px`;
  canvas.style.height = `${height}px`;
  if (ctx) {
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    ctx.clearRect(0, 0, width, height);
    ctx.fillStyle = HEATMAP_COLORS.panel;
    ctx.fillRect(0, 0, width, height);
  }

  if (
    width < 80 ||
    height < 80 ||
    data.dates.length === 0 ||
    data.strikes.length === 0 ||
    field.length !== data.strikes.length
  ) {
    return;
  }

  const nDays = data.dates.length;
  const nK = data.strikes.length;
  const z = field;
  const useClock = clock != null && clock.times.length === nDays;
  const zMax =
    useClock && clock
      ? terrainScale(z, columnAt(clock.times, '15:00') + 1)
      : zMaxOf(z);
  const m = HEATMAP_MARGIN;
  const iw = width - m.left - m.right;
  const ih = height - m.top - m.bottom;
  if (iw < 40 || ih < 40) return;

  const k0 = data.strikes[0]!;
  const kN = data.strikes[nK - 1]!;
  if (ctx) {
    paintField(
      ctx,
      data.strikes,
      z,
      zMax,
      m.left,
      m.top,
      iw,
      ih,
      useClock ? dpr : 1,
      useClock ? 1 : FIELD_DOWNSAMPLE
    );
  }

  svg.attr('viewBox', `0 0 ${width} ${height}`).attr('width', width).attr('height', height);
  const g = svg.append('g').attr('transform', `translate(${m.left},${m.top})`);

  const x = d3.scaleLinear().domain([0, Math.max(nDays - 1, 1)]).range([0, iw]);
  const y = d3.scaleLinear().domain([k0, kN]).range([ih, 0]);

  g.append('clipPath')
    .attr('id', clipId)
    .append('rect')
    .attr('width', iw)
    .attr('height', ih);
  const ov = g.append('g').attr('clip-path', `url(#${clipId})`);

  const coneLine = d3
    .line<number>()
    .x((_, i) => x(i))
    .y((v) => y(v))
    .curve(d3.curveMonotoneX);
  const priceLo = Math.min(k0, kN);
  const priceHi = Math.max(k0, kN);
  const ridgeFit: RidgeFit | undefined = useClock
    ? {
        merge: Math.max(medianStrikeGap(data.strikes) * 0.6, (data.spot || 1) * (8 / 7680)),
        link: Math.max(medianStrikeGap(data.strikes) * 1.6, (data.spot || 1) * (15 / 7680)),
        minRun: nDays >= 30 ? 10 : undefined,
      }
    : undefined;
  if (useClock && clock) {
    const levels: { price: number; name: string }[] = [
      { price: clock.plus2, name: '+2σ' },
      { price: clock.plus1, name: '+1σ' },
      { price: clock.minus1, name: '−1σ' },
      { price: clock.minus2, name: '−2σ' },
    ];
    const labelX = x(columnAt(clock.times, '11:15'));
    for (const level of levels) {
      if (level.price < priceLo || level.price > priceHi) continue;
      ov.append('line')
        .attr('x1', 0)
        .attr('x2', iw)
        .attr('y1', y(level.price))
        .attr('y2', y(level.price))
        .attr('stroke', HEATMAP_COLORS.cone)
        .attr('stroke-width', 1)
        .attr('stroke-dasharray', '4 3');
      g.append('text')
        .attr('x', labelX)
        .attr('y', y(level.price) - 3)
        .attr('text-anchor', 'middle')
        .attr('fill', '#475569')
        .attr('font-size', 11)
        .text(level.name);
    }
    const prints: { column: number; price: number }[] = [];
    for (let i = 0; i < clock.path.length; i++) {
      const price = clock.path[i];
      if (price == null || !Number.isFinite(price)) continue;
      prints.push({ column: i, price });
    }
    const spotPath = d3
      .line<{ column: number; price: number }>()
      .x((point) => x(point.column))
      .y((point) => y(point.price))
      .curve(d3.curveMonotoneX);
    const drawn = spotPath(prints);
    if (drawn) {
      ov.append('path')
        .attr('d', drawn)
        .attr('fill', 'none')
        .attr('stroke', '#111827')
        .attr('stroke-width', 1.15);
    }
    if (clock.asof) {
      const mark = x(columnAt(clock.times, clock.asof));
      ov.append('line')
        .attr('x1', mark)
        .attr('x2', mark)
        .attr('y1', 0)
        .attr('y2', ih)
        .attr('stroke', '#94a3b8')
        .attr('stroke-width', 1)
        .attr('stroke-dasharray', '2 2');
    }
  } else if (data.cone_upper.length === nDays && data.cone_lower.length === nDays && data.spot > 0) {
    const drawCone = (series: number[]): void => {
      const path = coneLine(series);
      if (!path) return;
      ov.append('path')
        .attr('d', path)
        .attr('fill', 'none')
        .attr('stroke', HEATMAP_COLORS.cone)
        .attr('stroke-width', 1.25)
        .attr('stroke-dasharray', '5 4');
    };
    const scaled = (band: number[], scale: number): number[] =>
      band.map((value) => data.spot + scale * (value - data.spot));
    const upper2 = scaled(data.cone_upper, 2);
    const lower2 = scaled(data.cone_lower, 2);
    for (const series of [upper2, data.cone_upper, data.cone_lower, lower2]) drawCone(series);

    const pctOf = (price: number): string =>
      `${((Math.abs(price - data.spot) / data.spot) * 100).toFixed(1)}%`;
    const place = (xAt: number, price: number, text: string, below: boolean): void => {
      const raw = y(price) + (below ? 11 : -4);
      const ly = Math.min(ih - 2, Math.max(10, raw));
      g.append('text')
        .attr('x', Math.min(iw - 4, Math.max(28, xAt)))
        .attr('y', ly)
        .attr('text-anchor', 'end')
        .attr('fill', HEATMAP_COLORS.cone)
        .attr('font-size', 10)
        .attr('font-weight', 600)
        .text(text);
    };
    const lastInside = (series: number[]): { index: number; price: number } | null => {
      let found: { index: number; price: number } | null = null;
      for (let i = 0; i < series.length; i++) {
        const price = series[i];
        if (price == null || price < priceLo || price > priceHi) continue;
        found = { index: i, price };
      }
      return found;
    };
    const upperLast = data.cone_upper[nDays - 1];
    const lowerLast = data.cone_lower[nDays - 1];
    const upper2Hit = lastInside(upper2);
    const lower2Hit = lastInside(lower2);
    if (upper2Hit) place(x(upper2Hit.index), upper2Hit.price, `+2σ ${pctOf(upper2Hit.price)}`, false);
    if (upperLast != null) place(iw - 4, upperLast, `+1σ ${pctOf(upperLast)}`, false);
    if (lowerLast != null) place(iw - 4, lowerLast, `−1σ ${pctOf(lowerLast)}`, true);
    if (lower2Hit) place(x(lower2Hit.index), lower2Hit.price, `−2σ ${pctOf(lower2Hit.price)}`, true);
  }

  if (!useClock && data.earnings) {
    const j = dateIndex(data.dates, data.earnings.date);
    if (j >= 0) {
      const ex = x(j);
      ov.append('line')
        .attr('x1', ex)
        .attr('x2', ex)
        .attr('y1', 0)
        .attr('y2', ih)
        .attr('stroke', HEATMAP_COLORS.sunrise)
        .attr('stroke-width', 1.5);
      g.append('text')
        .attr('x', ex)
        .attr('y', -4)
        .attr('text-anchor', 'middle')
        .attr('fill', HEATMAP_COLORS.sunrise)
        .attr('font-size', 10)
        .attr('font-weight', 700)
        .attr('letter-spacing', '0.6px')
        .text(earningsCaption(data.earnings));
    }
  }

  if (!useClock && data.spot > 0) {
    g.append('line')
      .attr('x1', 0)
      .attr('x2', iw)
      .attr('y1', y(data.spot))
      .attr('y2', y(data.spot))
      .attr('stroke', HEATMAP_COLORS.gold)
      .attr('stroke-width', 1.25)
      .attr('stroke-dasharray', '4 3');
    g.append('text')
      .attr('x', iw - 4)
      .attr('y', y(data.spot) - 5)
      .attr('text-anchor', 'end')
      .attr('fill', HEATMAP_COLORS.gold)
      .attr('font-size', 10)
      .attr('font-weight', 600)
      .text(`spot ${data.spot.toFixed(2)}`);
  }

  const ridgeLine = d3
    .line<RidgePoint>()
    .x((point) => x(point.column))
    .y((point) => y(point.price))
    .curve(d3.curveMonotoneX);
  const drawTracks = (tracks: RidgePoint[][], stroke: string, widthPx: number): void => {
    for (const track of tracks) {
      const path = ridgeLine(track);
      if (!path) continue;
      ov.append('path')
        .attr('d', path)
        .attr('fill', 'none')
        .attr('stroke', stroke)
        .attr('stroke-width', widthPx)
        .attr('stroke-linejoin', 'round')
        .attr('stroke-linecap', 'round');
    }
  };
  drawTracks(zeroContours(data.strikes, z, ridgeFit), HEATMAP_COLORS.zero, 1.15);
  drawTracks(gammaRidges(data.strikes, z, false, ridgeFit), HEATMAP_COLORS.trough, 1.35);
  drawTracks(gammaRidges(data.strikes, z, true, ridgeFit), HEATMAP_COLORS.peak, 1.5);

  const yAxis = d3.axisLeft(y).ticks(8).tickSize(-iw).tickFormat(d3.format(',.0f'));
  g.append('g')
    .call(yAxis)
    .call((s) => s.select('.domain').remove())
    .call((s) => s.selectAll('.tick line').attr('stroke', HEATMAP_COLORS.grid))
    .call((s) =>
      s
        .selectAll('.tick text')
        .attr('fill', HEATMAP_COLORS.muted)
        .attr('font-size', 11)
        .attr('font-variant-numeric', 'tabular-nums')
    );

  const xTicks = useClock && clock
    ? clock.times.flatMap((time, index) => (time.endsWith(':00') ? [{ index, label: time }] : []))
    : weekdayTicks(data.dates).map((index) => ({ index, label: formatMdTick(data.dates[index] ?? '') }));
  g.append('g')
    .attr('transform', `translate(0,${ih})`)
    .selectAll('text')
    .data(xTicks)
    .join('text')
    .attr('x', (tick) => x(tick.index))
    .attr('y', 16)
    .attr('text-anchor', 'middle')
    .attr('fill', HEATMAP_COLORS.muted)
    .attr('font-size', 10)
    .attr('font-variant-numeric', 'tabular-nums')
    .text((tick) => tick.label);

  const rampX = iw + 12;
  const rampW = 10;
  const rampSteps = 48;
  for (let p = 0; p < rampSteps; p++) {
    const t = 1 - (2 * p) / (rampSteps - 1);
    g.append('rect')
      .attr('x', rampX)
      .attr('y', (p / rampSteps) * ih)
      .attr('width', rampW)
      .attr('height', ih / rampSteps + 0.5)
      .attr('fill', rgbCss(fieldColor(t * zMax, zMax)));
  }
  g.append('text')
    .attr('x', rampX + rampW / 2)
    .attr('y', -4)
    .attr('text-anchor', 'middle')
    .attr('fill', HEATMAP_COLORS.muted)
    .attr('font-size', 9)
    .attr('font-variant-numeric', 'tabular-nums')
    .text('+');
  g.append('text')
    .attr('x', rampX + rampW / 2)
    .attr('y', ih + 12)
    .attr('text-anchor', 'middle')
    .attr('fill', HEATMAP_COLORS.muted)
    .attr('font-size', 9)
    .attr('font-variant-numeric', 'tabular-nums')
    .text('−');
}
